import {NextRouter, useRouter} from 'next/router';
import {useMemo} from 'react';
import Url, {qs} from 'url-parse';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {StaticLandingFreeSeriesMoreFetcher} from '../../../../common/fetchers/StaticLandingFreeSeriesMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import {twMerge} from '../../../../common/twMerge';
import useTypedSelector from '../../../../common/useTypedSelector';
import {DayOfWeekTab} from '../../../../design-system/organisms/DayOfWeekTab/DayOfWeekTab';
import {HeaderTitle} from '../../../../design-system/organisms/HeaderTitle/HeaderTitle';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {
  StaticLandingFreeSeriesSection,
  StaticLandingFreeSeriesSectionParam,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {selectIsSubtab} from '../../../../staticLanding/selector';
import {CommonSectionProps} from '../../type';

export function StaticLandingFreeSeries({
  section,
  children,
}: CommonSectionProps) {
  const isSubtab = useTypedSelector(state => selectIsSubtab({section, state}));
  const staticLandingFreeSeriesSection = section as Omit<
    StaticLandingFreeSeriesSection,
    'groups'
  >;
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const {
    param,
    tabList: _tabList,
    title,
    totalCount,
    promotionBanner,
  } = staticLandingFreeSeriesSection;
  const router = useRouter();

  const tabList = useMemo(
    () =>
      _tabList.map(({name, param: tabUid}) => {
        const {pathname, query} = getRouteWithParam({
          router,
          param,
          tabUid,
          isSubtab,
        });
        const queryStr = qs.stringify(query);
        return {
          name,
          param: tabUid,
          href: `${pathname}${queryStr ? `?${qs.stringify(query)}` : ''}`,
        };
      }),
    [_tabList, isSubtab, param, router]
  );

  const currentDayName = param.tab?.name;

  return (
    <div
      className="flex flex-col w-full grow"
      {...putTiaraObjectProps({
        customProps: {
          page_day: currentDayName,
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...(isSubtab && {
        [PageViewCustomPropsDataKey.TabType]: 'tab_type=landing_single',
      })}
    >
      {title && !isMobile && !isSubtab && (
        <HeaderTitle className="mt-20pxr mb-8pxr px-15pxr">{title}</HeaderTitle>
      )}
      {tabList && tabList.length > 0 && (
        <TabPositionSticky
          depth={isSubtab ? 3 : 1}
          disabled={!isMobile}
          isMobile={isMobile}
          isWebView={isWebView}
        >
          <DayOfWeekTab
            tabItems={tabList}
            currentTabItemParam={
              staticLandingFreeSeriesSection.param.tab?.param as string
            }
            onClickTabItem={(tabUid, e) => {
              aggregateAndSendLog({
                targetElem: e?.target as HTMLElement,
                actionName: '요일_클릭',
                actionType: 'Event',
                click: {
                  copy: tabList.find(day => day.param === tabUid)?.name,
                },
                customProps: {
                  user_action: 'click',
                },
              });
            }}
            className={twMerge('mb-10pxr px-10pxr')}
            {...putTiaraObjectProps({
              click: {
                layer1: isSubtab ? '서브탭2' : '서브탭',
              },
            })}
          />
        </TabPositionSticky>
      )}
      {isSubtab && promotionBanner && (
        <PromotionBannerContainer promotionBanner={promotionBanner} />
      )}
      {isGreaterThanZero(totalCount) && (
        <StaticLandingFreeSeriesMoreFetcher
          section={staticLandingFreeSeriesSection}
        >
          {children}
        </StaticLandingFreeSeriesMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </div>
  );
}

function getRouteWithParam({
  router,
  param,
  tabUid,
  isSubtab,
}: {
  router: NextRouter;
  param: StaticLandingFreeSeriesSectionParam;
  tabUid?: string;
  isSubtab: boolean;
}) {
  const {categoryUid} = param;

  let pathname, query;
  if (isSubtab) {
    // /menu/{menu_uid}/screen/{screen_uid}
    pathname = IS_CLIENT_SIDE
      ? window.location.pathname
      : new Url(router.asPath).pathname;
    // 서브탭인 경우 카테고리 uid를 변경하는 경우는 존재하지 않음
    query = {
      ...(tabUid && {tab_uid: tabUid}),
    };
  } else {
    pathname = `/landing/free-series/${categoryUid}`;
    query = {
      ...(tabUid && {tab_uid: tabUid}),
    };
  }

  return {pathname, query};
}
